import { InputStepper } from "@tine/designsystem-ui-react";
import { cn } from "@tine/designsystem-utils";
import { includes, isNumber } from "lodash-es";
import isFunction from "lodash-es/isFunction";
import { action, makeAutoObservable } from "mobx";
import { observer } from "mobx-react-lite";
import { useMemo, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { Link } from "wouter";

import useContainerWidth, { type WidthRanges } from "../../../common/hooks/useContainerWidth";
import { productImageUrl } from "../../../common/imageUtils";
import { queueImpression, sendProductClick } from "../../../common/tracking";
import { PageType, type Product, type ProductTileCallbacks } from "../../../common/types/productTypes";
import type { GAListInformation } from "../../../common/types/trackingTypes";
import { formatPrice, formatUnit } from "../../../common/utils";

import authStore from "../../../stores/auth/authStore";
import productStore from "../../../stores/product/productStore";
import { SEASONAL_PRODUCTS } from "../../../stores/product/productUtils";
import promotionStore from "../../../stores/promotionStore";
import uiStore from "../../../stores/uiStore";
import theme from "../../../themes/theme";
import AddToCartButton from "../../AddToCartButton";
import Badges from "../../Badges";
import Button from "../../Button";
import DisplayPrice from "../../DisplayPrice";
import Favorite from "../../Favorite";
import Labels from "../../Labels";
import PricePerItem from "../../PricePerItem";
import PriceBomb from "../PriceBomb";

type ProductTileProps = {
   product: Product;
   listInformation: GAListInformation;
   position: number;
   className?: string;
   callbacks?: ProductTileCallbacks;
};

type InternalStateStore = {
   qty: number;
   hovering: boolean;
   hasReportedVisibility: boolean;
};

type BreakpointSize = "thin" | "normal";
const ranges: WidthRanges<BreakpointSize> = {
   thin: [0, 190],
   normal: [190]
};
const ProductTile = ({ product, listInformation, position, className = "", callbacks = {} }: ProductTileProps) => {
   const imageRef = useRef<HTMLImageElement>(null);
   const tileRef = useRef<HTMLDivElement>(null);
   const { ref: containerRef, size: currentContainerSize } = useContainerWidth<BreakpointSize, HTMLDivElement>(ranges);
   const tileState: InternalStateStore = useMemo(
      () =>
         makeAutoObservable({
            qty: 1,
            hovering: false,
            hasReportedVisibility: false
         } satisfies InternalStateStore),
      []
   );

   const trackImpression = action((isVisible: boolean) => {
      if (isVisible && !tileState.hasReportedVisibility) {
         queueImpression(product, listInformation.name, position);
         // Allow other consumers to insert their own callback
         if (isFunction(callbacks.impression)) {
            callbacks.impression(product, listInformation.name, position);
         }
         tileState.hasReportedVisibility = true;
      }
   });

   const { ref } = useInView({
      triggerOnce: true,
      onChange: trackImpression
   });

   const checkIfWarn = action(() => {
      tileState.hovering = !authStore.isLoggedIn();
   });

   const removeWarning = action(() => {
      tileState.hovering = false;
   });

   const trackClick = () => {
      sendProductClick(product, listInformation, position);
      // Allow other consumers to insert their own callback
      if (isFunction(callbacks.click)) {
         callbacks.click(product, listInformation.name, position);
      }
   };

   const handleQuantityChange = action((qty: number) => {
      tileState.qty = qty;
      return qty;
   });

   const seasonalProduct = includes(SEASONAL_PRODUCTS, product.sku);

   const stepperAndButtonContainer = cn("flex w-full items-center justify-stretch gap-1", {
      "flex-col gap-2": currentContainerSize === "thin"
   });

   const inputStepper = cn(
      "flex-grow self-center [&>button]:h-[43px] [&>button]:w-[43px] disabled:[&>button]:bg-transparent [&>div]:h-[43px] [&>div]:min-w-[30px] [&>div]:max-w-[120px] [&>div]:flex-grow [&>div]:overflow-hidden",
      { "w-full [&>button]:flex-grow-0 [&>div]:max-w-[120px] [&>div]:flex-grow": currentContainerSize === "thin" }
   );

   const outerClassName = cn("h-full rounded-lg shadow-sm hover:shadow-md", className);

   return (
      <div ref={ref} className={outerClassName}>
         <div ref={containerRef} className="h-full min-h-[340px]">
            {/** biome-ignore lint/a11y/noStaticElementInteractions: maybe this should be a link? */}
            <div
               ref={tileRef}
               className={cn("group relative flex min-h-full flex-col justify-between rounded-lg bg-base-0 p-3", {
                  "z-10 shadow-xl": tileState.hovering
               })}
               onMouseEnter={checkIfWarn}
               onMouseLeave={removeWarning}
            >
               <div className="flex flex-col">
                  <div className="relative">
                     <div className="absolute right-0 flex flex-col">
                        {!product.expired && <Favorite sku={product.sku} listInformation={listInformation} position={position} />}
                        <Badges badges={product.badges} page={PageType.ProductListPage} />
                     </div>

                     <Labels labels={product.labels} page={PageType.ProductListPage} />

                     <Link tabIndex={-1} to={productStore.getUrl(product)} onClick={trackClick} data-testid="product-link">
                        <img
                           alt={`Bilde av ${product.name}`}
                           ref={imageRef}
                           loading="lazy"
                           className="mx-auto my-4 block aspect-square w-7/12 p-1 group-hover:p-0 group-hover:transition-[padding] group-hover:duration-150"
                           src={productImageUrl(product.sku, 130)}
                        />
                        {promotionStore.getPromotionsBySku(product.sku).map((promotion) => (
                           <PriceBomb size="small" key={promotion.banner}>
                              {promotion.banner}
                           </PriceBomb>
                        ))}
                     </Link>
                  </div>
                  <div className="mb-1 text-sm/[16px] font-medium">
                     <Link to={productStore.getUrl(product)} onClick={trackClick}>
                        {product.name}
                     </Link>
                  </div>
                  <div className="mb-3 text-xxs text-base-600">Art.nr. {product.sku}</div>
               </div>
               <div className="flex flex-col justify-end">
                  {authStore.isLoggedIn() && !product.expired && (
                     <>
                        <DisplayPrice
                           className="mb-1"
                           price={product.priceBeforeDiscount ?? product.price ?? 0}
                           newPrice={product.price ?? 0}
                           postPriceDetail={formatUnit(product.unit)}
                        />
                        <div className="flex flex-col gap-1 mb-2">
                           <PricePerItem className="text-xxs text-base-600" product={product} />
                           {isNumber(product.comparisonPrice) && theme.showComparisonPrice && !!product.comparisonUnit && (
                              <div className="mb-1 text-xxs text-base-600">
                                 {formatPrice(product.comparisonPrice)} per {product.comparisonUnit?.toLowerCase()}
                              </div>
                           )}
                        </div>
                     </>
                  )}
                  {!product.expired && authStore.isLoggedIn() && (
                     <div className={stepperAndButtonContainer}>
                        <InputStepper className={inputStepper} value={tileState.qty} onChange={handleQuantityChange} min={1} />

                        <AddToCartButton
                           product={product}
                           qty={tileState.qty}
                           imageRefFn={() => imageRef}
                           callbacks={callbacks}
                           listInformation={listInformation}
                           position={position}
                           size={currentContainerSize === "thin" ? "regular" : "iconOnly"}
                           className={currentContainerSize === "thin" ? "w-full" : ""}
                        />
                     </div>
                  )}
                  {!authStore.isLoggedIn() && (
                     <Button className="mt-4" variant="tertiary" size="compact" onClick={uiStore.showLoginModal}>
                        Logg inn for kjøp
                     </Button>
                  )}
                  {product.expired && authStore.isLoggedIn() && (
                     <p className="text-xs">
                        {seasonalProduct ? "Denne varen er utgått for sesongen" : "Denne varen er utgått fra sortimentet"}
                     </p>
                  )}
               </div>
            </div>
         </div>
      </div>
   );
};

export default observer(ProductTile);
